import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Alert, CircularProgress, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { fetchJson } from '../../../utils/fetchJson';
import { UserContext } from '../../../contexts/UserContext';

export default function PaysheetPage() {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [years, setYears] = useState([]);
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [selectedYearId, setSelectedYearId] = useState('');
  const [pricePerStudent, setPricePerStudent] = useState('');
  const [savingPrice, setSavingPrice] = useState(false);

  const canManage = useMemo(() => {
    if (user?.role === 'SystemAdmin') return true;
    const perms = user && Array.isArray(user.systemPermissions) ? user.systemPermissions : null;
    if (!perms) return true;
    if (perms.includes('*')) return true;
    return perms.includes('billing');
  }, [user]);

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    loadYears();
  }, []);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const data = await fetchJson('/api/subscriptions/paysheet');
      setRows((data?.rows || []).map((r, idx) => ({ id: r.id ?? idx, ...r })));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadYears() {
    try {
      const data = await fetchJson('/api/global-academic-years');
      const list = Array.isArray(data) ? data : (Array.isArray(data?.years) ? data.years : []);
      setYears(list);
      const current = list.find(y => !!y.isCurrent) || list[list.length - 1] || null;
      if (current?.id) {
        setSelectedYearId(String(current.id));
        if (current.pricePerStudent !== undefined && current.pricePerStudent !== null) {
          setPricePerStudent(String(current.pricePerStudent));
        }
      }
    } catch (_) {}
  }

  const openPriceDialog = async () => {
    if (!canManage) return;
    setPriceDialogOpen(true);
    await loadYears();
  };

  const savePrice = async () => {
    if (!selectedYearId) return;
    if (!canManage) return;
    setSavingPrice(true);
    setError('');
    try {
      await fetchJson(`/api/global-academic-years/${selectedYearId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pricePerStudent: Number(pricePerStudent || 0) })
      });
      setPriceDialogOpen(false);
      await loadYears();
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingPrice(false);
    }
  };

  const columns = [
    { field: 'schoolName', headerName: t('paysheet.school', 'School'), flex: 1, minWidth: 200 },
    { field: 'academicYearName', headerName: t('paysheet.academicYear', 'Academic Year'), width: 160 },
    { field: 'studentCount', headerName: t('paysheet.students', 'Students'), width: 110, type: 'number' },
    {
      field: 'pricePerStudent',
      headerName: t('paysheet.pricePerStudent', 'Price / student (XAF)'),
      width: 160,
      valueGetter: (params) => {
        const v = Number(params?.row?.pricePerStudent || 0);
        return v.toLocaleString();
      }
    },
    {
      field: 'amount',
      headerName: t('paysheet.amountDue', 'Amount Due (XAF)'),
      width: 170,
      valueGetter: (params) => {
        const v = Number(params?.row?.amount || 0);
        return v.toLocaleString();
      }
    },
    { field: 'status', headerName: t('paysheet.schoolStatus', 'School Status'), width: 130 }
  ];

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 1, mb: 2 }}>
        <Typography variant="h5">{t('paysheet.title', 'Paysheet')}</Typography>
        <Button variant="contained" onClick={openPriceDialog} disabled={!canManage}>
          {t('paysheet.setPrice', 'Set / Update Price per Student')}
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ textAlign: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <div style={{ height: 520, width: '100%' }}>
          <DataGrid rows={rows} columns={columns} pageSize={10} rowsPerPageOptions={[10, 25, 50]} />
        </div>
      )}

      <Dialog open={priceDialogOpen} onClose={() => setPriceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('paysheet.setPriceTitle', 'Set Price per Student (XAF)')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>{t('paysheet.academicYear', 'Academic Year')}</InputLabel>
              <Select
                label={t('paysheet.academicYear', 'Academic Year')}
                value={selectedYearId}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedYearId(v);
                  const found = years.find(y => String(y.id) === String(v));
                  if (found && found.pricePerStudent !== undefined && found.pricePerStudent !== null) {
                    setPricePerStudent(String(found.pricePerStudent));
                  }
                }}
              >
                {years.map(y => (
                  <MenuItem key={y.id} value={String(y.id)}>
                    {y.name}{y.isCurrent ? ` (${t('paysheet.current', 'Current')})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={t('paysheet.priceLabel', 'Price per student (XAF)')}
              type="number"
              value={pricePerStudent}
              onChange={(e) => setPricePerStudent(e.target.value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPriceDialogOpen(false)} disabled={savingPrice}>{t('paysheet.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={savePrice} disabled={savingPrice || !selectedYearId || !canManage}>
            {t('paysheet.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
